/**
 * SW2-2443 — how the offline project sync sends one queued write, and how it
 * tells "the server said no" from "the server could not be reached".
 *
 * Every request passes `{ replay: true }` so an unreachable server rejects here
 * rather than being queued again as a success (SW2-2321).
 */
import db from '../db/db';
import { isNetworkFailure } from './offlineFallback';

/** The request was answered, but not accepted. The item stays queued. */
export const FAILED = Symbol('offline-sync-failed');

/** The server could not be reached. The sync stops here. */
export class ConnectionLost extends Error {
  constructor(label, cause) {
    super(`Connection lost while syncing ${label}`);
    this.name = 'ConnectionLost';
    this.cause = cause;
  }
}

// `navigator.onLine` as well: some API wrappers refuse offline without making a
// request (uploadApi.create), so there is no axios error to classify.
const isUnreachable = (err) => isNetworkFailure(err) || !navigator.onLine;

/**
 * SW2-2459 — awaited before every queued write. The automatic sync sets it to wait until the user's
 * own requests have settled (foregroundRequests), so on a cellular uplink a background sync does
 * not hold up the page the inspector is looking at. A sync the user started has none.
 */
let beforeSend = null;

export const setBeforeSend = (fn) => {
  beforeSend = fn;
};

/**
 * SW2-2459 — how long one queued write may take before it counts as a lost connection. The HTTP
 * client has no timeout, and on weak cellular a request can stall without ever failing; with one
 * sync at a time, that stalled the whole engine until the page was reloaded. Generous, because a
 * finding's photos go in its request. A write that does land after this is sent again on the retry
 * — the accepted lost-response case (SW2-2458).
 */
const SEND_TIMEOUT_MS = 120000;
const STEP_TIMEOUT_MS = 600000;

class SyncTimeout extends Error {
  constructor(label) {
    super(`Timed out syncing ${label}`);
    this.name = 'SyncTimeout';
  }
}

function withTimeout(label, promise, ms) {
  let timer;

  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new SyncTimeout(label)), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Run the hook a sync set for before each write. For a write that doesn't go through `send` (a
 * finding's photos, `runStep`), so it is checked too (PR #156 review).
 */
export async function beforeWrite() {
  if (beforeSend) await beforeSend();
}

/** Send one queued write: its result, or FAILED if the server refused it. */
export async function send(label, request) {
  if (beforeSend) await beforeSend();

  try {
    return await withTimeout(label, request(), SEND_TIMEOUT_MS);
  } catch (err) {
    if (err instanceof SyncTimeout) throw new ConnectionLost(label, err);

    if (isUnreachable(err)) {
      throw new ConnectionLost(label, err);
    }

    console.error(`Offline sync: ${label} was not accepted`, err);

    return FAILED;
  }
}

/**
 * Run a step that handles server rejections itself (syncPendingFindingPhotos)
 * but must still stop the sync when the server is unreachable.
 */
export async function runStep(label, step) {
  try {
    return await withTimeout(label, step(), STEP_TIMEOUT_MS);
  } catch (err) {
    if (err instanceof SyncTimeout) throw new ConnectionLost(label, err);

    if (!(err instanceof ConnectionLost) && isUnreachable(err)) {
      throw new ConnectionLost(label, err);
    }

    throw err;
  }
}

export const isPending = (row) => Boolean(row?.sync) && row.sync.synced === false;

/**
 * SW2-2446 — a local row's `sync` after an ONLINE save of that row.
 *
 * Anything queued before the save (offline work not yet synced) stays queued and pending: marking
 * the row synced, or replacing its payload, hid or dropped that work, and the next sync then
 * reported nothing left and deleted the offline copy. Only a row whose queue was already empty is
 * marked synced. Other fields the sync records on `sync` (`noteIds`, `serverId`, …) are kept.
 *
 * `edit` is for a save that overwrites the record (a finding edit, a placement move, a note edit).
 * Each queued edit carries the whole record as it was then, and the sync replays the queue in
 * order, so the older offline edit would land on the server after this newer one and undo it.
 * Queuing the online edit behind it makes the replay end on the newest version; the server gets it
 * once more, with the same content. Only when something is queued: with an empty queue the server
 * already has the latest. A save that only adds (a new note, a signature) passes no `edit`: queuing
 * it would send it twice.
 */
export const syncAfterOnlineSave = (sync, edit) => {
  const queued = (sync && sync.payload) || [];

  if (queued.length === 0) {
    return { ...(sync || {}), payload: [], synced: true };
  }

  return { ...sync, payload: edit ? [...queued, edit] : queued, synced: false };
};

/**
 * SW2-2446 — after the sync sends the first `count` entries of a row's queue, take exactly those off
 * the row as it is NOW and return what is left. The sync works from a snapshot of the queue, and a
 * save can land while a request is in flight (an online edit queued behind, or an offline save);
 * writing the snapshot's remainder back erased it, and marking the row synced hid it. The row is
 * marked synced only when nothing is left. `fields` are written in the same transaction.
 */
export const takeSentEntries = (table, key, count, fields = {}) =>
  db.transaction('rw', table, async () => {
    const row = await table.get(key);

    if (!row) return [];

    const rest = (row.sync?.payload || []).slice(count);

    await table.update(key, { ...fields, 'sync.payload': rest, 'sync.synced': rest.length === 0 });

    return rest;
  });

export const hasPendingCreate = (finding) =>
  isPending(finding) && finding.sync.payload?.[0]?.method === 'create';
